/**
 * MobileProfileFAB - Floating Action Button + BottomSheet for mobile profile view
 * Extracted from App.tsx for modularization
 */
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import BottomSheet from '../common/BottomSheet';
import ProfilePanel, {
  type ProfileDiff,
  type ProfileConflict,
  type RecentChange,
  type EditorPick,
  type FirebaseUser,
  type CartItem
} from './ProfilePanel';
import type { MasterProfile } from '../../types/profile';
import type { RecentChanges } from '../../types/conversation';

interface MobileProfileFABProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  masterProfile: Partial<MasterProfile>;
  recentChanges?: RecentChanges | null;
  profileDiff?: unknown;
  profileConflicts?: ProfileConflict[];
  coppaBlocked: boolean;
  isProfileLoading: boolean;
  profileError: string | null;
  onUndo: (path: string) => void;
  onUpdateProfile: (profile: MasterProfile) => void;
  editorPicks?: EditorPick[];
  isEditorPicksLoading: boolean;
  /** Whether editor picks are from affiliate sources (Amazon) */
  isEditorPicksAffiliate?: boolean;
  onRefreshEditorPicks: () => void;
  autoSave: boolean;
  language: string;
  showAddressFormForSample: boolean;
  onCancelAddressForm: () => void;
  user?: FirebaseUser | null;
  cartItems?: CartItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onAddToCart?: (item: any) => void | Promise<void>;
  onPhotoUpload: (file: File) => void;
  onViewDetails?: (pick: EditorPick) => void;
  onAskAI?: (productName: string) => void;
}

export default function MobileProfileFAB({
  isOpen,
  onOpen,
  onClose,
  masterProfile,
  recentChanges,
  profileDiff,
  profileConflicts,
  coppaBlocked,
  isProfileLoading,
  profileError,
  onUndo,
  onUpdateProfile,
  editorPicks,
  isEditorPicksLoading,
  isEditorPicksAffiliate,
  onRefreshEditorPicks,
  autoSave,
  language,
  showAddressFormForSample,
  onCancelAddressForm,
  user,
  cartItems,
  onAddToCart,
  onPhotoUpload,
  onViewDetails,
  onAskAI,
}: MobileProfileFABProps) {
  const { t } = useTranslation('common');

  // First-time tooltip — show once per user, auto-dismiss after 5s or on FAB click
  const [showTooltip, setShowTooltip] = useState(false);
  useEffect(() => {
    const key = 'heseda_profile_fab_hint_shown';
    if (localStorage.getItem(key)) return;
    const timer = setTimeout(() => setShowTooltip(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem('heseda_profile_fab_hint_shown', '1');
  };

  useEffect(() => {
    if (!showTooltip) return;
    const timer = setTimeout(dismissTooltip, 6000);
    return () => clearTimeout(timer);
  }, [showTooltip]);
  
  // Convert App.tsx recentChanges format to ProfilePanel RecentChange[] format
  const formattedRecentChanges: RecentChange[] | undefined = useMemo(() => {
    if (!recentChanges?.diff) return undefined;
    
    return Object.entries(recentChanges.diff).map(([path, change]) => ({
      path,
      newVal: change.new,
    }));
  }, [recentChanges]);

  return (
    <>
      <button 
        className="open-profile-fab" 
        onClick={() => {
          dismissTooltip();
          onOpen();
        }}
        aria-label={t('profile.openProfile')}
        title={t('profile.viewMasterProfile')}
      >
        {masterProfile?.identity?.photoURL ? (
          <img 
            src={masterProfile.identity.photoURL} 
            alt="Profile" 
            className="fab-profile-photo"
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="fab-icon">👤</span>
        )}
        <span className="fab-text">{t('profile.profileLabel')}</span>
        <span className="fab-expand-hint" aria-hidden="true">▲</span>
      </button>

      {showTooltip && (
        <button type="button" className="fab-tooltip" aria-label={t('profile.tapToViewProfile', 'Tap to view your skin profile')} onClick={dismissTooltip}>
          <span className="fab-tooltip__arrow" />
          {t('profile.tapToViewProfile', 'Tap to view your skin profile')}
        </button>
      )}

      <BottomSheet
        isOpen={isOpen}
        onClose={onClose}
        title={t('profile.masterProfile')}
        size="lg"
      >
        <ProfilePanel
          masterProfile={masterProfile}
          recentChanges={formattedRecentChanges}
          diff={profileDiff as ProfileDiff | undefined}
          conflicts={profileConflicts}
          coppaBlocked={coppaBlocked}
          isExtracting={isProfileLoading}
          profileError={profileError}
          onUndo={onUndo}
          onUpdateProfile={onUpdateProfile}
          editorPicks={editorPicks}
          isEditorPicksLoading={isEditorPicksLoading}
          isEditorPicksAffiliate={isEditorPicksAffiliate}
          onRefreshEditorPicks={onRefreshEditorPicks}
          autoSave={autoSave}
          language={language}
          showAddressFormForSample={showAddressFormForSample}
          onCancelAddressForm={onCancelAddressForm}
          user={user}
          hideHeader={true}
          cartItems={cartItems}
          onAddToCart={onAddToCart as (item: CartItem) => void}
          onPhotoUpload={onPhotoUpload}
          onViewDetails={onViewDetails}
          onAskAI={onAskAI}
        />
      </BottomSheet>
    </>
  );
}
